import { createTheme } from '@mui/material/styles'
import { breakpointsTheme } from './breakpoints'
import { palette } from './palette'
import { typography } from './typography'
import { TABLE_ROW_HEIGHT } from 'app/constants'

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: palette.backgroundColor,
    },
    primary: {
      main: palette.primary.main,
      light: palette.primary.light,
      dark: palette.primary.dark,
    },
  },
  shape: {
    borderRadius: 6,
  },
  breakpoints: breakpointsTheme.breakpoints,
  typography,
  components: {
    MuiTextField: {
      defaultProps: {
        autoComplete: 'off',
        fullWidth: true,
        slotProps: {
          inputLabel: {
            shrink: true,
            style: { color: 'rgba(0, 0, 0, 0.87)' },
          },
        },
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-input.MuiOutlinedInput-input': {
            height: '20px',
            paddingTop: '6px',
            paddingBottom: '2px',
          },
          '& fieldset': {
            border: '1px solid rgba(0, 0, 0, 0.38)',
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              border: `1px solid ${palette.primary.main}`,
            },
            '&.Mui-focused fieldset': {
              border: `1px solid ${palette.primary.main}`,
            },
          },
          '& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused': {
            color: palette.primary.main,
          },
          '& .MuiOutlinedInput-root.Mui-disabled': {
            '&:hover fieldset': {
              border: '1px solid rgba(0, 0, 0, 0.26)',
            },
          },
          '& .MuiOutlinedInput-root.Mui-error': {
            backgroundColor: 'rgba(211, 47, 47, 0.03)',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
          userSelect: 'none',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          minHeight: `${TABLE_ROW_HEIGHT * 3}px`,
          boxShadow: '1px 1px 5px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          position: 'sticky',
          top: 0,
          zIndex: 1,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: `${TABLE_ROW_HEIGHT}px`,
          '&:last-child td': { borderColor: 'transparent' },
          '&:last-child th': { borderColor: 'transparent' },
          userSelect: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingLeft: '4px',
          paddingRight: '4px',
          '&.MuiTableCell-head': {
            backgroundColor: palette.grey.light,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'pointer',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '10px',
          [breakpointsTheme.breakpoints.up('xl')]: {
            fontSize: '12px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '3px 4px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiSvgIcon-root': { fontSize: 14 },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: '-8px',
          '&:last-of-type': {
            marginRight: 0,
          },
          '&:hover span': {
            color: palette.primary.main,
          },
          '&.Mui-disabled:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled span': {
            color: 'rgba(0, 0, 0, 0.26)',
          },
          '&.Mui-disabled:hover span': {
            color: 'rgba(0, 0, 0, 0.26)',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '3px',
          paddingBottom: '3px',
          transition: 'background-color 0.3s ease-in-out',
          '&:hover p': {
            transition: '0.3s ease-in-out',
            color: palette.primary.main,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          lineHeight: '13px',
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: 1000,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(5px)',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
          '& div.MuiDialogContent-root': { padding: '8px 24px 12px' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '18px',
          paddingTop: '8px',
          paddingBottom: '4px',
          color: palette.backgroundColor,
          backgroundColor: palette.primary.main,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: palette.grey.light,
        },
      },
    },
  },
})

export { theme }
